"use client";

import { useState, useEffect } from "react";
import { m, useReducedMotion } from "framer-motion";
import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";

import SectionHeader from "@/components/common/SectionHeader";
import Button from "../common/Button";
import Image from "next/image";

import {
  mobileLight,
  mobileDark,
  desktopLight,
  desktopDark,
  smallSlots,
  tabletSlots,
  desktopSlots,
  largeSlots,
  largestSlots,
} from "@/constants/AboutData.js";

// helper function to define different slots- image sizes and positions
function useResponsiveSlots() {
  const [slots, setSlots] = useState(desktopSlots);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 900) {
        setSlots(smallSlots);
        setMobile(true);
      } else if (window.innerWidth < 1280) {
        setSlots(tabletSlots);
        setMobile(true);
      } else if (window.innerWidth < 1800) {
        setSlots(desktopSlots);
        setMobile(false);
      } else if (window.innerWidth < 2100) {
        setSlots(largeSlots);
        setMobile(false);
      } else {
        setSlots(largestSlots);
        setMobile(false);
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return { slots, mobile };
}

export default function ImageCarousel() {
  const isdark = useIsDark();

  const [images, setImages] = useState(desktopLight);
  const [order, setOrder] = useState(images);
  const [ready, setReady] = useState(false);
  const [fading, setFading] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [freezeSlot, setFreezeSlot] = useState(null);

  const { slots, mobile } = useResponsiveSlots();
  const prefersReduced = useReducedMotion();

  const svgWidth = 1120; // internal coordinate system width
  const svgHeight = 447; // internal coordinate system height

  useEffect(() => {
    const newImages = mobile
      ? isdark
        ? mobileDark
        : mobileLight
      : isdark
        ? desktopDark
        : desktopLight;

    setImages(newImages);
    setOrder(newImages);
  }, [isdark, mobile]);

  useEffect(() => {
    // Preload and fully decode all images before showing the carousel
    const preloadAll = async () => {
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              const im = new window.Image();
              im.src = img.src.src;

              // wait for load + decode to finish
              if (im.decode) {
                im.decode().then(resolve).catch(resolve);
              } else {
                im.onload = resolve;
                im.onerror = resolve;
              }
            })
        )
      );
      setReady(true);
    };

    preloadAll();
  }, []);

  useEffect(() => {
    if (prefersReduced) return;

    const interval = setInterval(() => {
      const last = order[4];
      if (!last) return;

      setMovingId(last.id);
      setFreezeSlot({ ...slots[5] });
      setFading(true);

      setTimeout(() => {
        setOrder((prev) => [prev[4], ...prev.slice(0, -1)]);

        setTimeout(() => {
          setFading(false);
          setMovingId(null);
          setFreezeSlot(null);
        }, 800);
      }, 300);
    }, 2800);

    return () => clearInterval(interval);
  }, [prefersReduced, slots, order]);

  return (
    <>
      {ready && (
        <div className="relative w-full max-w-[2000px] flex flex-col justify-center items-center mx-auto">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`} // internal coordinate system
            className="w-full h-auto pointer-events-none z-0 invisible sm:visible"
          >
            <defs>
              <linearGradient
                id="ellipseGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={
                    isdark ? "rgba(0,0,0,0.07)" : "rgba(255, 255, 255, 0.07)"
                  }
                />
                <stop
                  offset="74.5%"
                  stopColor={
                    isdark ? "rgba(83,2,80,0.7)" : "rgba(156, 0, 187, 1)"
                  }
                />
                <stop
                  offset="100%"
                  stopColor={
                    isdark ? "rgba(202,0,195,0.25)" : "rgba(223, 39, 223, 1)"
                  }
                />
              </linearGradient>
            </defs>

            <ellipse
              cx={svgWidth / 2}
              cy={svgHeight / 2}
              rx={svgWidth * 0.45}
              ry={svgHeight * 0.4}
              stroke="url(#ellipseGradient)"
              strokeWidth="4"
              fill="transparent"
            />
          </svg>

          <div
            className="flex absolute top-0 tablet:top-2 xl:-top-5 laptop:top-2 flex-col justify-center items-center laptop:mb-20"
            style={{
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
              willChange: "transform, opacity",
            }}
          >
            <SectionHeader
              title={"Have a project in mind?"}
              description={
                "We’d love to hear about it. Let’s explore how we can help bring your ideas to life with clarity, precision, and purpose."
              }
              wrapperClassName="max-w-[450px] tablet:max-w-[690px] mb-1 mobile:mb-3 md:mb-1 tablet:mb-3 xl:mb-5 px-0"
              titleClassName="sm:text-[22px] laptop:text-[40px]"
              descriptionClassName="text-paragraph w-full text-[14px] xl:text-[20px] xl:uppercase mt-0 mobile:mt-2 xl:mt-3 laptop:mt-6"
            />
            <Button
              label="Let's Talk"
              href="/contact#contact-form"
              className={twMerge(
                "xl:w-[109px] xl:h-12",
                isdark ? "" : "bg-[#DF27DF]"
              )}
            />
          </div>

          <div
            className={
              "absolute top-11/12 pt-100 small:pt-60 mobile:pt-45 mobile2:pt-35 sm:pt-0"
            }
          >
            {order.map((img, index) => {
              console.log(order);
              // const baseW = slots[2].width;
              // const baseH = slots[2].height;
              const w = slots[index].width;
              const h = slots[index].height;
              // const scaleX = w / baseW;
              // const scaleY = h / baseH;

              const currentIndex = order.findIndex((o) => o.id === img.id);
              const isMoving = movingId === img.id;

              let targetX = slots[index].x;
              let targetY = slots[index].y;

              if (isMoving && fading && freezeSlot) {
                if (currentIndex === 0) {
                  targetX = freezeSlot.x;
                  targetY = freezeSlot.y;
                } else {
                  targetX = slots[currentIndex].x;
                  targetY = slots[currentIndex].y;
                }
              } else {
                targetX = slots[index].x;
                targetY = slots[index].y;
              }
              const targetOpacity = isMoving && fading ? 0 : 1;

              return (
                <m.div
                  key={img.id}
                  animate={{
                    x: slots[index].x,
                    y: slots[index].y,
                    opacity: targetOpacity,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 64,
                    damping: 26,
                    mass: 0.9,
                    bounce: 0,
                    restDelta: 0.0005,
                    opacity: {
                      type: "tween",
                      duration: 0.5,
                      ease: "easeInOut",
                    },
                  }}
                  className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${index === 2 ? "z-10" : "z-0"}`}
                  style={{
                    willChange: "transform",
                    transform: "translateZ(0)",
                    backfaceVisibility: "hidden",
                    WebkitBackfaceVisibility: "hidden",
                  }}
                >
                  <m.div
                    className="relative rounded-[7px] overflow-hidden transform-gpu"
                    initial={{
                      width: slots[index].width,
                      height: slots[index].height,
                    }}
                    animate={{
                      width: slots[index].width,
                      height: slots[index].height,
                    }}
                    style={{
                      width: slots[index].width,
                      height: slots[index].height,
                      willChange: "transform,opacity",
                      transform: "translateZ(0)",
                      backfaceVisibility: "hidden",
                      WebkitBackfaceVisibility: "hidden",
                      transformOrigin: "center center",
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 64,
                      damping: 26,
                      mass: 0.9,
                      bounce: 0,
                      restDelta: 0.0005,
                    }}
                  >
                    <Image
                      src={img.src}
                      alt="carousel image"
                      width={slots[index].width}
                      height={slots[index].height}
                      sizes={ // More accurate sizes based on the slot definitions from AboutData.js
                        index === 2 // Center image
                          ? "(max-width: 899px) 291px, (max-width: 1279px) 333px, (max-width: 1799px) 426px, (max-width: 2099px) 511px, 613px"
                          : // Side images (using slot 1 as a representative size for side images)
                            "(max-width: 899px) 110px, (max-width: 1279px) 132px, (max-width: 1799px) 160px, (max-width: 2099px) 192px, 230px"
                      }
                      priority={index === 2}
                      loading={index === 2 ? "eager" : "lazy"}
                      decoding="async"
                      className="object-cover will-change-auto"
                      style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        inset: 0,
                        transform: "translateZ(0)",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                      }}
                    />
                    <m.div
                      initial={false}
                      animate={{ opacity: index === 2 ? 0.0001 : 1 }}
                      transition={{ duration: 0.2 }}
                      className="absolute inset-0 bg-overlay pointer-events-none"
                      style={{
                        willChange: "opacity",
                        transform: "translateZ(0)",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                      }}
                    />
                  </m.div>
                </m.div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
